import React, { useEffect, useState } from "react";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import LineChart from "../../../Components/Charts/LineChart";
import GenericTable from "../../../Components/Tables/GenericTable";
import totalFleetIcon from "../../../Assets/Icons/wallet.svg";
import efficiencyIcon from "../../../Assets/Icons/energy_savings_leaf.svg";
import distanceIcon from "../../../Assets/Icons/measuring_tape.svg";
import fuelIcon from "../../../Assets/Icons/local_gas_station.svg";
import transactionIcon from "../../../Assets/Icons/payments_spend.svg";
import costIcon from "../../../Assets/Icons/measuring_tape_cost.svg";
import DateFilter from "../../../Components/DateFilter";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import { getManagerReports } from "../../../API/FleetManager/Reports";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ImageAvatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "5px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: getAvatarColor(name),
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      style={{
        width: 24,
        height: 24,
        borderRadius: "5px",
        objectFit: "cover",
        flexShrink: 0,
      }}
      onError={() => setFailed(true)}
    />
  );
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const Reports = () => {
  const { from, to } = useDateFilter();
  const [reportsData, setReportsData] = useState(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getManagerReports(startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          setReportsData(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load reports:", err);
      }
    };

    fetchReports();
  }, [from, to]);

  const stats = reportsData?.stats || {};

  const vehicleDataSource = (reportsData?.top_vehicles_by_fuel_usage || []).map(
    (vehicle, index) => ({
      key: String(vehicle.vehicle_id ?? index),
      vehicleId: vehicle.vehicle_id ?? "-",
      licensePlate: vehicle.license_plate || "-",
      driver: vehicle.driver_name || "-",
      fuelType: vehicle.fuel_type_name || "-",
      volume: vehicle.total_volume ? `${vehicle.total_volume} L` : "-",
      cost: formatCurrency(vehicle.total_amount),
      transactions: vehicle.transaction_count ?? 0,
      avgCost: formatCurrency(vehicle.avg_cost_per_liter),
    }),
  );

  const uniqueVehicleFuelTypes = [
    ...new Set(vehicleDataSource.map((item) => item.fuelType)),
  ];

  const vehicleTypeFilters = uniqueVehicleFuelTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const vehicleColumns = [
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "License Plate",
      dataIndex: "licensePlate",
      key: "licensePlate",
      enableSearch: true,
    },
    {
      title: "Driver Name",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      render: (type) => <span className="type-badge">{type}</span>,
      filters: vehicleTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
    },
    {
      title: "Fuel Volume",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat(a.volume.replace(/[^0-9.]/g, "")) || 0) -
        (parseFloat(b.volume.replace(/[^0-9.]/g, "")) || 0),
    },
    {
      title: "Fuel Cost",
      dataIndex: "cost",
      key: "cost",
      sorter: (a, b) =>
        (parseFloat(a.cost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.cost.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Transactions",
      dataIndex: "transactions",
      key: "transactions",
      sorter: (a, b) => (a.transactions || 0) - (b.transactions || 0),
    },
    {
      title: "Avg Cost/L",
      dataIndex: "avgCost",
      key: "avgCost",
      sorter: (a, b) =>
        (parseFloat(a.avgCost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.avgCost.replace(/[$,]/g, "")) || 0),
    },
  ];

  const transactionsDataSource = (reportsData?.transactions || []).map(
    (txn) => ({
      key: String(txn.id),
      date: txn.date || "-",
      time: txn.time || "-",
      txnId: txn.txn_id || "-",
      driverName: txn.driver_name || "-",
      driverAvatar: resolveAvatarUrl(txn.driver_avatar),
      vehicleId: txn.vehicle_id || "-",
      vendor: txn.fuel_vendor_name || "-",
      vendorAvatar: resolveAvatarUrl(txn.fuel_vendor_avatar),
      fuelType: txn.fuel_type_name || "-",
      volume: txn.volume ? `${txn.volume}L` : "-",
      amount: formatCurrency(txn.amount),
      status: capitalize(txn.status) || "-",
    }),
  );

  const uniqueFuelTypes = [
    ...new Set(transactionsDataSource.map((item) => item.fuelType)),
  ];

  const typeFilters = uniqueFuelTypes.map((type) => ({
    text: type,
    value: type,
  }));

  const vendorFilters = [
    ...new Set(transactionsDataSource.map((item) => item.vendor)),
  ].map((vendor) => ({ text: vendor, value: vendor }));

  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.2",
          }}
        >
          <span>{text}</span>
          <span style={{ color: "#71717A" }}>{record.time}</span>
        </div>
      ),
    },
    {
      title: "Txn ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },
    {
      title: "Driver Name",
      dataIndex: "driverName",
      key: "driverName",
      enableSearch: "true",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageAvatar src={record.driverAvatar} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: "true",
      filters: vendorFilters,
      onFilter: (value, record) => record.vendor === value,
      render: (text, record) => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <ImageAvatar src={record.vendorAvatar} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: typeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (type) => <span className="type-badge">{type}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        (parseFloat(a.amount.replace(/[^0-9.-]+/g, "")) || 0) -
        (parseFloat(b.amount.replace(/[^0-9.-]+/g, "")) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Approved", value: "Approved" },
        { text: "Denied", value: "Denied" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Approved" ? "approved" : "denied"}`}
        >
          {text}
        </span>
      ),
    },
  ];

  const performanceTrend = reportsData?.performance_trend;

  return (
    <div className="fleet-manager report">
      <div className="reports-controls">
        <div className="section-title">Reports</div>
        <div className="date-filter-wrapper">
          <DateFilter />
        </div>
      </div>

      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(stats.total_spend?.value)}
            percentage={stats.total_spend?.percentage ?? undefined}
            isIncrease={stats.total_spend?.is_increase}
            subtitle="vs. last month"
            icon={totalFleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Avg Efficiency"
            value={`${stats.avg_efficiency_km_per_l?.value ?? 0} km/L`}
            percentage={stats.avg_efficiency_km_per_l?.percentage ?? undefined}
            isIncrease={stats.avg_efficiency_km_per_l?.is_increase}
            subtitle="vs. last month"
            icon={efficiencyIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Distance"
            value={`${stats.total_distance_km?.value ?? 0} KM`}
            percentage={stats.total_distance_km?.percentage ?? undefined}
            isIncrease={stats.total_distance_km?.is_increase}
            subtitle="vs. last month"
            icon={distanceIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Litres"
            value={`${stats.total_litres?.value ?? 0} L`}
            percentage={stats.total_litres?.percentage ?? undefined}
            isIncrease={stats.total_litres?.is_increase}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Transactions"
            value={stats.total_transactions?.value ?? 0}
            percentage={stats.total_transactions?.percentage ?? undefined}
            isIncrease={stats.total_transactions?.is_increase}
            subtitle="vs. last month"
            icon={transactionIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Cost Per KM"
            value={formatCurrency(stats.cost_per_km?.value)}
            percentage={stats.cost_per_km?.percentage ?? undefined}
            isIncrease={stats.cost_per_km?.is_increase}
            subtitle="vs. last month"
            icon={costIcon}
          />
        </div>
      </div>

      <div className="row-graph">
        <div className="graph">
          <LineChart
            graphData={{
              labels: performanceTrend?.labels || [],
              Spend: (performanceTrend?.spend || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Volume: (performanceTrend?.volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#4F46E5", "#EA580C"]}
            graphTitle="Performance"
            selectTenByDefault={false}
            units={["$", "L"]}
            classNameCSS="fuel-spend"
          />
        </div>
      </div>

      <div className="vehicle-table">
        <div className="vehicle-table">
          <GenericTable
            title="Top Vehicle by Fuel Usage"
            dataSource={vehicleDataSource}
            columns={vehicleColumns}
          />
        </div>
      </div>
      <div className="transaction-table">
        <GenericTable
          title="Transactions"
          dataSource={transactionsDataSource}
          columns={transactionColumns}
          enableRowSelection={false}
          onSelectionChange={(keys, rows) => console.log(keys, rows)}
        />
      </div>
    </div>
  );
};

export default Reports;
